import { useRef, useCallback } from 'react';
import { getEnrichedParts } from '../hooks/useEnrichedTracks';

const AUDIO_FILE_RE = /\.(mp3|wav|wave|aif|aiff|flac|m4a|ogg|aac|alac)$/i;

// True when the tab's source selection is a single folder — the only case
// where the Name/Folders list toggle is meaningful (playlists, Library and
// picked files carry their own order).
export function isFolderSource(sourcePaths) {
    return Array.isArray(sourcePaths) && sourcePaths.length === 1 && !AUDIO_FILE_RE.test(sourcePaths[0]);
}

// Memoized displayed-label lookup ("Artist - Title" via enrichment, filename
// fallback). getEnrichedParts does path normalization + parsing per call, so
// sorting a large list needs this cache; it resets when the enrichment index
// reloads.
export function useDisplayLabel(enrichedMap) {
    const cacheRef = useRef({ map: null, labels: new Map() });
    return useCallback((p) => {
        const st = cacheRef.current;
        if (st.map !== enrichedMap) { st.map = enrichedMap; st.labels = new Map(); }
        let v = st.labels.get(p);
        if (v === undefined) { v = getEnrichedParts(enrichedMap, p).label || ''; st.labels.set(p, v); }
        return v;
    }, [enrichedMap]);
}

// Group visible track rows under subfolder header rows, preserving the user's
// own folder organization: non-track rows first (unchanged), then root-level
// tracks (no header), then each subfolder A-Z under a synthetic
// { status: 'folder-header', folderLabel } row — tracks A-Z by displayed
// label within each group.
export function groupRowsByFolder(rows, rootDir, trackPathOf, labelFor) {
    const root = (rootDir || '').replace(/\\/g, '/').replace(/\/+$/, '');
    const others = [];
    const groups = new Map(); // relative folder -> rows
    for (const row of rows) {
        const p = trackPathOf(row);
        if (!p) { others.push(row); continue; }
        const norm = p.replace(/\\/g, '/');
        const dir = norm.slice(0, norm.lastIndexOf('/'));
        const rel = (dir === root || dir.startsWith(root + '/')) ? dir.slice(root.length).replace(/^\//, '') : dir;
        if (!groups.has(rel)) groups.set(rel, []);
        groups.get(rel).push(row);
    }
    const cmp = (a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
    const byLabel = (a, b) => cmp(labelFor(trackPathOf(a)), labelFor(trackPathOf(b)));
    const keys = [...groups.keys()].sort((a, b) => (a === '' ? -1 : b === '' ? 1 : cmp(a, b)));
    const out = [...others];
    for (const k of keys) {
        if (k !== '') out.push({ status: 'folder-header', folderLabel: k, msg: k });
        out.push(...[...groups.get(k)].sort(byLabel));
    }
    return out;
}

// Stable A-Z re-sort of the visible track rows by displayed label, non-track
// rows first. Used for non-folder sources when the user picks "Name" over
// the source's own order ("Added").
export function sortTrackRowsByLabel(rows, trackPathOf, labelFor) {
    const others = [];
    const tracks = [];
    for (const row of rows) (trackPathOf(row) ? tracks : others).push(row);
    const cmp = (a, b) => String(labelFor(trackPathOf(a)) || '')
        .localeCompare(String(labelFor(trackPathOf(b)) || ''), undefined, { sensitivity: 'base', numeric: true });
    return [...others, ...tracks.sort(cmp)];
}
